import "@/assets/css/app.css";
import NotFound404 from "@lib/components/NotFound";
import { lazy, StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { initIntercomSettings } from "./account/boot-intercom";
import ErrorBoundary from "./lib/components/ErrorBoundary";
import { initializeAccessibilityMonitoring } from "./lib/utils/accessibility-monitor";

// Lazy load app components to reduce initial bundle size
const AccountApp = lazy(() => import("./account/App"));
const MerchantApp = lazy(() => import("./merchant/App"));
const PlatformApp = lazy(() => import("./platform/App"));
const LedgerApp = lazy(() => import("./ledger/App"));
const CertifyApp = lazy(() => import("./certify/App"));

// Simple loading fallback
const AppLoading = () => (
  <div className="flex h-screen w-full items-center justify-center bg-gray-100 dark:bg-zinc-950">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900 dark:border-zinc-700 dark:border-t-zinc-300"></div>
  </div>
);

initIntercomSettings();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

const root = ReactDOM.createRoot(rootElement);

async function initializeApp() {
  try {
    const title = import.meta.env.VITE_REACT_APP_TITLE;
    if (title) document.title = title;

    // Define a variable for dynamic component selection
    let App = AccountApp; // Default to AccountApp

    switch (import.meta.env.VITE_REACT_APP_DASHBOARD_TYPE) {
      case "merchant":
        App = MerchantApp;
        break;
      case "platform":
        App = PlatformApp;
        break;
      case "ledger":
        App = LedgerApp;
        break;
      case "certify":
        App = CertifyApp;
        break;
    }

    // Add error handling for development only
    if (import.meta.env.DEV) {
      window.addEventListener("error", (event) => {
        console.error("Global error caught:", event.error);
      });

      window.addEventListener("unhandledrejection", (event) => {
        console.error("Unhandled promise rejection:", event.reason);
      });
    }

    root.render(
      <StrictMode>
        <ErrorBoundary>
          <Suspense fallback={<AppLoading />}>
            <App />
          </Suspense>
        </ErrorBoundary>
      </StrictMode>,
    );

    // Initialize accessibility monitoring after the app renders
    // Only runs in development mode to avoid production overhead
    initializeAccessibilityMonitoring();
  } catch (error) {
    console.error("Initialization failed:", error);

    root.render(
      <StrictMode>
        <NotFound404 />
      </StrictMode>,
    );
  }
}

// Call the initializeApp function to start the app
initializeApp();
